/**
 * Engine / server liveness (VF-35).
 *
 * The license-server decides (services/engine-liveness.ts: freshest of licence pull, registration refresh and
 * heartbeat, one threshold table) and returns `liveness`, `last_contact_at` and `contact_age_seconds` on both
 * /internal/engines and /internal/installations. The Engines and Servers pages only RENDER that verdict: neither
 * page may derive "online" from `last_seen_at` (the 6-hourly registration refresh) or a private age threshold.
 */
import type { Liveness } from '../api';
import type { Band } from '../components/ui/StatusChip';

export function bandForLiveness(l: Liveness | undefined): Band {
  switch (l) {
    case 'online': return 'healthy';
    case 'degraded': return 'warning';
    case 'offline': return 'critical';
    default: return 'neutral';           // 'never' or an older server that does not send the field
  }
}

export function labelForLiveness(l: Liveness | undefined): string {
  switch (l) {
    case 'online': return 'online';
    case 'degraded': return 'degraded';
    case 'offline': return 'offline';
    default: return 'never seen';
  }
}

/** "just now" / "5m ago" / "3h ago" / "2d ago" from a server-computed age in seconds. */
export function fmtContactAge(ageSeconds: number | null | undefined): string {
  if (ageSeconds == null) return 'never';
  const min = Math.floor(ageSeconds / 60);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/** Online or degraded: the engine was heard from recently enough to count as live. */
export const isLive = (l: Liveness | undefined): boolean => l === 'online' || l === 'degraded';
